import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeyedValue } from '@core/models/interfaces/common';
import { LeadNote } from '@core/models/interfaces/lead/lead-notes';
import { NoteFilters, NoteGroup, NoteLead, NotePolicy, NoteStatus } from '@core/models/interfaces/note';
import { UserDetail } from '@core/models/interfaces/user';
import { NoteService } from '@core/services/note.service';
import { UserService } from '@core/services/user.service';
import { NoteManagementPolicyComponent } from '@feature/note/components/note-management-policy/note-management-policy.component';
import { NoteTableComponent } from '@feature/note/components/note-table/note-table.component';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomCalendarComponent } from '@shared/components/custom-calendar/custom-calendar.component';
import { CustomLoadingComponent } from '@shared/components/custom-loading/custom-loading.component';
import { CustomPaginatorComponent } from '@shared/components/custom-paginator/custom-paginator.component';
import { NoteLeadMultiSelectDropdownComponent } from '@shared/components/dropdown/note-lead-multi-select-dropdown/note-lead-multi-select-dropdown.component';
import { PolicyMultiSelectDropdownComponent } from '@shared/components/dropdown/policy-multi-select-dropdown/policy-multi-select-dropdown.component';
import { UserMultiSelectDropdownComponent } from '@shared/components/dropdown/user-multi-select-dropdown/user-multi-select-dropdown.component';
import { EmptyComponent } from '@shared/components/empty/empty.component';
import { SearchInputComponent } from '@shared/components/search-input/search-input.component';
import { capitalizeDropdownTitle } from '@shared/utils/capitalizeDropdownTitle.util';
import { isEmptyObject, pickNotEmpty } from '@shared/utils/dataObject.util';
import { AppState } from '@state/app.state';
import { selectUserInfo } from '@state/auth/auth.selectors';
import moment from 'moment';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectLazyLoadEvent, MultiSelectModule } from 'primeng/multiselect';
import { forkJoin, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-note-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    ButtonComponent,
    TranslateModule,
    MultiSelectModule,
    EmptyComponent,
    SearchInputComponent,
    CustomPaginatorComponent,
    CustomLoadingComponent,
    CustomCalendarComponent,
    UserMultiSelectDropdownComponent,
    NoteTableComponent,
    NoteManagementPolicyComponent,
    PolicyMultiSelectDropdownComponent,
    NoteLeadMultiSelectDropdownComponent
  ],
  templateUrl: './note-management.component.html',
  styleUrl: './note-management.component.scss'
})
export class NoteManagementComponent implements OnInit, OnDestroy {
  readonly translatePrefix = 'note-management.';
  readonly unsubscribe$ = new Subject();
  private fetchNotes$ = new Subject<void>();

  totalNotes: number = 0;
  notes: LeadNote[] = [];

  totalGroups: number = 0;
  noteGroups: NoteGroup[] = [];

  totalNotesStatuses: number = 0;
  noteStatuses: NoteStatus[] = [];
  totalUsers = 0;
  users: UserDetail[] = [];

  leads: NoteLead[] = [];
  totalLeads = 0;

  policies: NotePolicy[] = [];
  totalPolicies = 0;

  isLoading = false;
  isSort = false;
  isFilter = false;
  dateAddedSort: KeyedValue = { title: 'Date added: Newest first', value: '-createdAt' };
  dateAddedOptions: KeyedValue[] = [
    { title: 'Date added: Newest first', value: '-createdAt' },
    { title: 'Date added: Oldest first', value: 'createdAt' },
    { title: 'Date updated: Newest first', value: '-updatedAt' },
    { title: 'Date updated: Oldest first', value: 'updatedAt' }
  ];
  groupBy = '';
  groupByOptions: KeyedValue[] = [
    { title: 'None', value: '' },
    { title: 'Lead', value: 'LEAD' }
  ];
  pagination = {
    page: 0,
    size: 10
  };
  paginationGroup = {
    page: 0,
    size: 10
  };

  filters: NoteFilters = {
    leadsFilter: [],
    policyFilter: [],
    keyword: '',
    statusFilter: [],
    addedByFilter: [],
    datesFilter: []
  };

  userInfo: UserDetail;
  loadingNoteStatus: boolean = false;
  pageNoteStatuses: number = 0;
  keywordStatus: string = '';
  keywordNote: string = '';

  get noteStatusesDrop() {
    return this.noteStatuses.map(item => ({
      id: item.id,
      name: item.name,
      color: item.color,
      backgroundColor: item.backgroundColor
    }));
  }

  

  constructor(
    private noteService: NoteService,
    private userService: UserService,
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.initNoteLead();
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      if (params['pagination']) {
        this.pagination = JSON.parse(params['pagination']);
      }
      if (params['filters']) {
        const tempFilter = JSON.parse(params['filters']);
        this.filters = { ...this.filters, ...tempFilter };
        this.isFilter = this.hasAnyFilter();
      }
      if (params['dateAddedSort']) {
        this.dateAddedSort = JSON.parse(params['dateAddedSort']);
        this.isSort = this.hasSelectedSortFilter();
      }
      if (params['groupBy']) {
        this.groupBy = params['groupBy'];
        if (this.groupBy === 'LEAD') {
          this.isFilter = true;
        }
      }
      if (params['paginationGroup']) {
        this.paginationGroup = JSON.parse(params['paginationGroup']);
      }
    });
    this.store
      .select(selectUserInfo)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(userInfo => {
        if (userInfo) {
          this.userInfo = userInfo;
        }
      });

    forkJoin([this.userService.getUsers()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: results => {
          const [userRes] = results;
          this.users = userRes.data.content;
          this.totalUsers = userRes.data.total;
        }
      });
    this.fetchLeadsFilter();
    this.fetchPoliciesFilter();
    this.fetchDataByGroup();
    this.fetchNoteStatuses('SEARCH');
  }

  initNoteLead() {
    this.fetchNotes$
      .pipe(
        tap(() => (this.isLoading = true)), // cleaner loading state
        switchMap(() => {
          const payload = this.buildLeadNotesPayload();
          return this.noteService.getLeadNotes(payload);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.notes = res.data.content;
          this.totalNotes = res.data.total;
          this.onUpdatedURL();
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  /**
   * Helper method to build request payload for getLeadNotes
   */
  private buildLeadNotesPayload() {
    const { keyword, statusFilter, datesFilter, addedByFilter, leadsFilter, policyFilter } = this.filters;

    const statusIds = statusFilter.map(item => item.id);
    const createdBys = addedByFilter.map(item => item.id);
    const leadIds = leadsFilter.map(item => item.id);
    const policyIds = policyFilter.map(item => item.id);

    const dateAddedFrom = this.formatDate(datesFilter?.[0], 'start');
    const dateAddedTo = this.formatDate(datesFilter?.[1], 'end');

    return {
      sorts: `-isPin|${this.dateAddedSort.value}`,
      keyword,
      statusIds,
      createdBys,
      leadIds,
      policyIds,
      page: this.pagination.page,
      size: this.pagination.size,
      createdFrom: dateAddedFrom,
      createdTo: dateAddedTo
    };
  }

  /**
   * Helper method to format date consistently
   */
  private formatDate(date: Date | undefined, boundary: 'start' | 'end'): string | undefined {
    return date ? moment(date)[boundary === 'start' ? 'startOf' : 'endOf']('date').toDate().toISOString() : undefined;
  }

  hasAnyFilter(): boolean {
    return Object.values(this.filters).some(arr => arr.length > 0);
  }

  hasSelectedSortFilter(): boolean {
    return this.dateAddedSort.value !== '-createdAt';
  }

  onUpdatedURL() {
    const safeFilters = pickNotEmpty(this.filters);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        filters: isEmptyObject(safeFilters) ? null : JSON.stringify(safeFilters),
        pagination: this.groupBy ? null : JSON.stringify(this.pagination),
        paginationGroup: this.groupBy ? JSON.stringify(this.paginationGroup) : null,
        dateAddedSort: this.dateAddedSort.value === '-createdAt' ? null : JSON.stringify(this.dateAddedSort),
        groupBy: this.groupBy ? this.groupBy : null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  fetchNoteStatuses(type: 'SCROLL' | 'SEARCH') {
    if (type === 'SEARCH') this.loadingNoteStatus = true;
    const params = {
      page: this.pageNoteStatuses,
      size: 10,
      keyword: this.keywordStatus
    };
    this.noteService
      .getNoteStatuses(params)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          if (type === 'SCROLL') {
            this.noteStatuses = [...this.noteStatuses, ...res.data.content];
          } else {
            this.noteStatuses = res.data.content;
          }
          this.loadingNoteStatus = false;
          this.totalNotesStatuses = res.data.total;
        },
        error: () => {
          this.loadingNoteStatus = false;
        }
      });
  }

  onLazyLoadNoteStatus(event: MultiSelectLazyLoadEvent) {
    if (this.loadingNoteStatus) return;
    const { last } = event;
    if (last >= this.noteStatuses?.length && this.noteStatuses?.length < this.totalNotesStatuses) {
      this.pageNoteStatuses++;
      this.loadingNoteStatus = true;
      this.fetchNoteStatuses('SCROLL');
    }
  }

  onSearch(keyword: string) {
    this.pageNoteStatuses = 0;
    this.noteStatuses = [];
    this.keywordStatus = keyword;
    this.fetchNoteStatuses('SEARCH');
  }

  get statusSelected() {
    return this.filters.statusFilter.map(status => status.name).join(', ');
  }

  get addedBySelected() {
    return this.filters.addedByFilter.map(user => user.fullName).join(', ');
  }

  get leadSelected() {
    return this.filters.leadsFilter.map(lead => lead.id).join(', ');
  }

  get policySelected() {
    return this.filters.policyFilter.map(policy => policy.id).join(', ');
  }

  fetchLeadNotes() {
    this.fetchNotes$.next();
  }

  fetchNoteGroups() {
    this.isLoading = true;

    const { keyword, statusFilter, datesFilter, addedByFilter, leadsFilter, policyFilter } = this.filters;

    const statusIds = statusFilter.map(item => item.id);
    const createdBys = addedByFilter.map(item => item.id);

    const leadIds = leadsFilter.map(item => item.id);
    const policyIds = policyFilter.map(item => item.id);

    const dateAddedFrom = datesFilter?.[0] ? moment(datesFilter[0]).startOf('date').toDate().toISOString() : undefined;
    const dateAddedTo = datesFilter?.[1] ? moment(datesFilter[1]).endOf('date').toDate().toISOString() : undefined;
    this.noteService
      .getLeadNotesGroupBy({
        page: this.paginationGroup.page,
        size: this.paginationGroup.size,
        sorts: `-isPin|${this.dateAddedSort.value}`,
        keyword: keyword,
        statusIds: statusIds,
        createdBys: createdBys,
        leadIds: leadIds,
        policyIds: policyIds,
        createdFrom: dateAddedFrom,
        createdTo: dateAddedTo
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.noteGroups = res.data.content;
          this.totalGroups = res.data.total;
          this.onUpdatedURL();
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onSearchNote(keyword: string) {
    this.filters = { ...this.filters, keyword };
    this.keywordNote = keyword;
    this.resetPagination();
    this.fetchDataByGroup();
  }

  onChangeFilters() {
    this.filters = { ...this.filters };
    this.resetPagination();
    this.fetchDataByGroup();
  }

  fetchDataByGroup() {
    if (this.groupBy === 'LEAD') {
      this.fetchNoteGroups();
    } else {
      this.fetchLeadNotes();
    }
  }

  onChangeGroupBy() {
    this.fetchDataByGroup();
  }

  resetPagination() {
    this.pagination = {
      page: 0,
      size: 10
    };
    this.paginationGroup = {
      page: 0,
      size: 10
    };
  }

  onResetFilter() {
    this.filters = {
      leadsFilter: [],
      policyFilter: [],
      keyword: '',
      statusFilter: [],
      addedByFilter: [],
      datesFilter: []
    };
    this.groupBy = '';
    this.resetPagination();
    this.fetchLeadNotes();
  }

  get statusIdsFromFilter() {
    return this.filters.statusFilter.map(item => item.id);
  }

  get addedByIdsFromFilter() {
    return this.filters.addedByFilter.map(item => item.id);
  }

  get leadIdsFromFilter() {
    return this.filters.leadsFilter.map(item => item.id);
  }

  get policyIdsFromFilter() {
    return this.filters.policyFilter.map(item => item.id);
  }

  fetchPoliciesFilter() {
    this.noteService
      .getPolicyForNotesManagement({
        // leadIds: this.leadIdsFromFilter,
        // policyIds: this.policyIdsFromFilter
      })
      .subscribe({
        next: res => {
          this.totalPolicies = res.data.total;
          this.policies = res.data.content;
        }
      });
  }

  fetchLeadsFilter() {
    this.noteService
      .getLeadForNotesManagement({
        // leadIds: this.leadIdsFromFilter,
        // policyIds: this.policyIdsFromFilter
      })
      .subscribe({
        next: res => {
          this.totalLeads = res.data.total;
          this.leads = res.data.content;
        }
      });
  }

  get resetDisabled() {
    return (
      !this.filters.statusFilter.length &&
      !this.filters.addedByFilter.length &&
      !this.filters.datesFilter.length &&
      !this.filters.leadsFilter.length &&
      !this.filters.policyFilter.length
    );
  }

  deleteNoteLead() {
    this.resetPagination();
    this.fetchNoteGroups();
  }

  getDateAddedSortOptionTitle() {
    return capitalizeDropdownTitle(this.dateAddedSort);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
